import React, { useState } from 'react';
import { useGetAlarmsQuery, useUpdateSensorAlarmMutation } from '../services/alarmApi';
import { useGetSensorListQuery } from '../services/sensorApi';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';

function getErrorMessage(error: unknown): string {
  if (typeof error === 'string') return error;
  if (error && typeof error === 'object' && 'message' in error && typeof (error as any).message === 'string') {
    return (error as any).message;
  }
  return 'Unknown error';
}

const boolToText = (val: boolean) => (val ? 'Yes' : 'No');
const boolToInput = (val: boolean, onChange: (v: boolean) => void) => (
  <input type="checkbox" checked={val} onChange={e => onChange(e.target.checked)} />
);

const defaultAlarm = {
  high_alarm: '',
  low_alarm: '',
  delay: '',
  repeat: false,
  auto_reset: false,
  high_enable: false,
  low_enable: false,
  alarms_disabled: false,
};

export default function AlarmsPage() {
  const user = useSelector((state: RootState) => state.auth.user);
  const { data: sensors, isLoading: sensorsLoading, isError: sensorsError, error: sensorsErrorObj } = useGetSensorListQuery();
  const { data: alarms, isLoading: alarmsLoading, isError: alarmsError, error: alarmsErrorObj, refetch } = useGetAlarmsQuery(undefined, { skip: !user });
  const [editSensorId, setEditSensorId] = useState<number | null>(null);
  const [editAlarm, setEditAlarm] = useState<any>(null);
  const [updateSensorAlarm, { isLoading: isUpdating }] = useUpdateSensorAlarmMutation();
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Map alarms by sensor_id for quick lookup
  const alarmMap = (alarms && Array.isArray(alarms)) ? alarms.reduce((acc: any, alarm: any) => {
    acc[alarm.sensor_id] = alarm;
    return acc;
  }, {}) : {};

  const handleEdit = (sensorId: number) => {
    setEditSensorId(sensorId);
    setEditAlarm(alarmMap[sensorId] ? { ...alarmMap[sensorId] } : { ...defaultAlarm, sensor_id: sensorId });
  };

  const handleCancel = () => {
    setEditSensorId(null);
    setEditAlarm(null);
  };

  const handleSave = async () => {
    if (!editAlarm) return;
    await updateSensorAlarm({ sensorId: editAlarm.sensor_id, settings: editAlarm });
    await refetch(); // Ensure the table updates with the latest data
    setEditSensorId(null);
    setEditAlarm(null);
    setSuccessMessage('Alarm settings updated successfully!');
    setTimeout(() => setSuccessMessage(null), 2000);
  };

  const handleFieldChange = (field: string, value: any) => {
    setEditAlarm((prev: any) => ({ ...prev, [field]: value }));
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Thresholds</h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Monitor and manage sensor thresholds
        </p>
        {successMessage && (
          <div className="mt-4 px-4 py-2 bg-green-600 text-white rounded shadow">
            {successMessage}
          </div>
        )}
      </div>
      {(sensorsLoading || alarmsLoading) && (
        <div className="text-gray-700 dark:text-gray-200">Loading sensors and alarms...</div>
      )}
      {(sensorsError || alarmsError) && (
        <div className="text-red-600 dark:text-red-400">
          Error loading data: {getErrorMessage(sensorsErrorObj || alarmsErrorObj)}
        </div>
      )}
      {sensors && Array.isArray(sensors) && (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-white">
            <thead className="bg-gray-50 dark:bg-gray-900 text-white">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-white uppercase tracking-wider">Sensor Name</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-white uppercase tracking-wider">Serial No</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-white uppercase tracking-wider">High Alarm</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-white uppercase tracking-wider">Low Alarm</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-white uppercase tracking-wider">Delay (min)</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-white uppercase tracking-wider">Repeat</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-white uppercase tracking-wider">Auto Reset</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-white uppercase tracking-wider">High Enabled</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-white uppercase tracking-wider">Low Enabled</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-white uppercase tracking-wider">Alarms Disabled</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-white uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700 text-white">
              {sensors.map((sensor: any) => {
                const alarm = alarmMap[sensor.id] || { ...defaultAlarm, sensor_id: sensor.id };
                const isEditing = editSensorId === sensor.id;
                return (
                  <tr key={sensor.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 text-white">
                    <td className="px-4 py-2 whitespace-nowrap text-white">{sensor.name}</td>
                    <td className="px-4 py-2 whitespace-nowrap text-white">{sensor.serialNo}</td>
                    {isEditing ? (
                      <>
                        <td className="px-4 py-2 whitespace-nowrap">
                          <input type="number" value={editAlarm.high_alarm} onChange={e => handleFieldChange('high_alarm', e.target.value)} className="w-20 px-1 bg-gray-800 text-white" />
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap">
                          <input type="number" value={editAlarm.low_alarm} onChange={e => handleFieldChange('low_alarm', e.target.value)} className="w-20 px-1 bg-gray-800 text-white" />
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap">
                          <input type="number" value={editAlarm.delay} onChange={e => handleFieldChange('delay', e.target.value)} className="w-16 px-1 bg-gray-800 text-white" />
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap">{boolToInput(editAlarm.repeat, v => handleFieldChange('repeat', v))}</td>
                        <td className="px-4 py-2 whitespace-nowrap">{boolToInput(editAlarm.auto_reset, v => handleFieldChange('auto_reset', v))}</td>
                        <td className="px-4 py-2 whitespace-nowrap">{boolToInput(editAlarm.high_enable, v => handleFieldChange('high_enable', v))}</td>
                        <td className="px-4 py-2 whitespace-nowrap">{boolToInput(editAlarm.low_enable, v => handleFieldChange('low_enable', v))}</td>
                        <td className="px-4 py-2 whitespace-nowrap">{boolToInput(editAlarm.alarms_disabled, v => handleFieldChange('alarms_disabled', v))}</td>
                        <td className="px-4 py-2 whitespace-nowrap">
                          <button onClick={handleSave} className="mr-2 px-2 py-1 bg-green-600 text-white rounded" disabled={isUpdating}>Save</button>
                          <button onClick={handleCancel} className="px-2 py-1 bg-gray-400 text-white rounded">Cancel</button>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-4 py-2 whitespace-nowrap">{alarm.high_alarm}</td>
                        <td className="px-4 py-2 whitespace-nowrap">{alarm.low_alarm}</td>
                        <td className="px-4 py-2 whitespace-nowrap">{alarm.delay}</td>
                        <td className="px-4 py-2 whitespace-nowrap">{boolToText(alarm.repeat)}</td>
                        <td className="px-4 py-2 whitespace-nowrap">{boolToText(alarm.auto_reset)}</td>
                        <td className="px-4 py-2 whitespace-nowrap">{boolToText(alarm.high_enable)}</td>
                        <td className="px-4 py-2 whitespace-nowrap">{boolToText(alarm.low_enable)}</td>
                        <td className="px-4 py-2 whitespace-nowrap">{boolToText(alarm.alarms_disabled)}</td>
                        <td className="px-4 py-2 whitespace-nowrap">
                          <button onClick={() => handleEdit(sensor.id)} className="px-2 py-1 bg-blue-600 text-white rounded">Edit</button>
                        </td>
                      </>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}